import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { MapPin, Calendar, Music, Printer, Mail, Phone } from 'lucide-react';
import SOCIAL_PLATFORMS from '../constants/socialIcons';

const API_URL = process.env.REACT_APP_API_URL || '';

const SOCIAL_META = Object.fromEntries(
  SOCIAL_PLATFORMS.map(({ key, label, Icon, atSign }) => [key, { label, Icon, atSign }])
);

function extractHandle(url, key) {
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    const segments = parsed.pathname.replace(/\/$/, '').split('/').filter(Boolean);
    const last = segments[segments.length - 1] || '';

    if (key === 'website_url') {
      return parsed.hostname.replace(/^www\./, '');
    }
    if (key === 'spotify_url' || key === 'apple_music_url') {
      // These URLs don't have simple handles — just show the domain
      return parsed.hostname.replace(/^(open\.|music\.)/, '');
    }
    if (!last) return parsed.hostname;

    const { atSign } = SOCIAL_META[key] || {};
    if (atSign) {
      return last.startsWith('@') ? last : `@${last}`;
    }
    return last;
  } catch {
    return url;
  }
}

function formatGigDate(dateStr, timeStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const formatted = date.toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  if (!timeStr) return formatted;
  const [h, m] = timeStr.split(':');
  const t = new Date();
  t.setHours(parseInt(h, 10), parseInt(m, 10));
  const time = t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${formatted} at ${time}`;
}

function GigPrintSheet() {
  const { gigUrl } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/gigs/${gigUrl}/print-data`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => { setError('Failed to load gig data'); setLoading(false); });
  }, [gigUrl]);

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'system-ui' }}>Loading…</div>;
  if (error) return <div style={{ padding: '3rem', color: 'red', fontFamily: 'system-ui' }}>{error}</div>;
  if (!data) return null;

  const primary = data.primary_color || '#667eea';
  const secondary = data.secondary_color || '#764ba2';
  const bandName = data.band_name || data.gig_name;
  const gigDate = formatGigDate(data.gig_date, data.gig_time);
  const socials = Object.entries(SOCIAL_META)
    .filter(([key]) => data[key])
    .map(([key, meta]) => ({ ...meta, handle: extractHandle(data[key], key) }));

  return (
    <>
      <style>{`
        @page { size: A4 portrait; margin: 15mm; }
        body { margin: 0; font-family: system-ui, -apple-system, sans-serif; }
        @media screen {
          body { background: #e5e7eb; }
          .print-page { min-height: 267mm; }
        }
        @media print {
          .no-print { display: none !important; }
          .print-page {
            margin: 0 !important;
            box-shadow: none !important;
            min-height: 0 !important;
          }
          html { font-size: 13px; }
          .print-qr-img { width: 160px !important; height: 160px !important; }
          body { background: white; }
        }
      `}</style>

      {/* Toolbar — hidden on print */}
      <div className="no-print" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: '#1a1a2e', color: 'white',
        padding: '0.75rem 1.5rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: '1rem',
      }}>
        <a
          href="/dashboard/requests"
          style={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'none', fontSize: '0.9rem' }}
        >
          ← Back
        </a>
        <span style={{ fontWeight: '600', fontSize: '0.95rem' }}>
          Gig Promo Sheet — {bandName}
        </span>
        <button
          onClick={() => window.print()}
          style={{
            padding: '0.5rem 1.25rem',
            background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,
            color: 'white', border: 'none', borderRadius: '8px',
            cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem',
          }}
        >
          <Printer size={15} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} /> Print / Save as PDF
        </button>
      </div>

      {/* A4 sheet */}
      <div className="print-page" style={{
        maxWidth: '210mm',
        margin: '60px auto 32px',
        padding: '14mm 16mm',
        background: 'white',
        color: '#1a1a1a',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
      }}>

        {/* Header band strip */}
        <div style={{
          background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,
          borderRadius: '12px',
          padding: '1.75rem',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          marginBottom: '1.75rem',
          flexShrink: 0,
        }}>
          {data.logo_url ? (
            <img
              src={data.logo_url}
              alt="Band logo"
              style={{ width: '80px', height: '80px', objectFit: 'contain', borderRadius: '10px', background: 'rgba(255,255,255,0.15)', padding: '6px', flexShrink: 0 }}
            />
          ) : (
            <div style={{ width: '80px', height: '80px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Music size={40} color="white" />
            </div>
          )}
          <div>
            <h1 style={{ margin: '0 0 0.2rem', fontSize: '2rem', fontWeight: '800', lineHeight: 1.1 }}>{bandName}</h1>
            {data.tagline && (
              <p style={{ margin: 0, opacity: 0.88, fontSize: '1rem', fontStyle: 'italic' }}>{data.tagline}</p>
            )}
          </div>
        </div>

        {/* Promo photo banner */}
        {data.promo_photo_url && (
          <div style={{ marginBottom: '1.75rem', borderRadius: '10px', overflow: 'hidden', flexShrink: 0, height: '180px' }}>
            <img
              src={data.promo_photo_url}
              alt="Promo"
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover',
                objectPosition: `${data.promo_photo_x ?? 50}% ${data.promo_photo_y ?? 50}%`,
                transform: `scale(${(data.promo_photo_zoom || 100) / 100})`,
                transformOrigin: 'center center',
                display: 'block',
              }}
            />
          </div>
        )}

        {/* Gig details card */}
        <div style={{ marginBottom: '2rem', padding: '1.25rem 1.5rem', background: '#f0f2f5', borderRadius: '10px', flexShrink: 0, color: '#1a1a1a' }}>
          <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.2rem', color: primary }}>{data.gig_name}</h2>
          {data.venue_name && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.45rem', color: '#1a1a1a' }}>
              <MapPin size={14} color={primary} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                <strong>{data.venue_name}</strong>
                {data.venue_location && <span style={{ color: '#444' }}> — {data.venue_location}</span>}
              </span>
            </div>
          )}
          {gigDate && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', color: '#1a1a1a' }}>
              <Calendar size={14} color={primary} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{gigDate}</span>
            </div>
          )}
        </div>

        {/* QR code — centre piece */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.5rem 0', textAlign: 'center' }}>
          <p style={{ margin: '0 0 0.4rem', fontWeight: '800', fontSize: '1.3rem', color: '#222', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Request a Song Tonight!
          </p>
          <p style={{ margin: '0 0 1.5rem', color: '#666', fontSize: '0.95rem' }}>
            Scan the QR code with your phone to browse songs and make a request
          </p>
          <img
            src={data.qr_data_uri}
            alt="QR code to request songs"
            className="print-qr-img"
            style={{ width: '210px', height: '210px', borderRadius: '12px', border: `4px solid ${primary}` }}
          />
          <p style={{ margin: '1rem 0 0', fontFamily: 'monospace', color: '#555', fontSize: '0.85rem' }}>
            {data.audience_url}
          </p>
        </div>

        {/* Booking info */}
        {data.show_booking_info && (data.booking_email || data.booking_phone) && (
          <div style={{ marginTop: '1.75rem', padding: '1.1rem 1.25rem', border: `2px solid ${primary}`, borderRadius: '10px', flexShrink: 0, color: '#1a1a1a' }}>
            <p style={{ margin: '0 0 0.6rem', fontWeight: '700', color: primary, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Booking Enquiries
            </p>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', fontSize: '0.95rem', color: '#1a1a1a' }}>
              {data.booking_email && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}><Mail size={14} color={primary} /> {data.booking_email}</span>}
              {data.booking_phone && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}><Phone size={14} color={primary} /> {data.booking_phone}</span>}
            </div>
          </div>
        )}

        {/* Social links */}
        {data.show_social_media && socials.length > 0 && (
          <div style={{ marginTop: '1.25rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem', justifyContent: 'center', flexShrink: 0 }}>
            {socials.map(({ label, Icon, handle }) => (
              <span key={label} style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                padding: '0.3rem 0.75rem',
                background: '#e8eaf0', color: '#1a1a1a',
                borderRadius: '999px', fontSize: '0.8rem',
              }}>
                <Icon size={13} color="#444" /> <strong>{label}:</strong> {handle}
              </span>
            ))}
          </div>
        )}

        {/* Footer text */}
        {data.footer_text && (
          <p style={{ margin: '1.25rem 0 0', textAlign: 'center', color: '#888', fontSize: '0.82rem', fontStyle: 'italic', flexShrink: 0 }}>
            {data.footer_text}
          </p>
        )}
      </div>
    </>
  );
}

export default GigPrintSheet;
